/**
 * Image utility functions for the Banner Ad Creator.
 */

/**
 * Load an image from a URL or data URL.
 * @param {string} src - Image source URL
 * @returns {Promise<HTMLImageElement>}
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Convert a File/Blob to a data URL.
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a canvas to a Blob.
 * @param {HTMLCanvasElement} canvas
 * @param {string} [type='image/png']
 * @param {number} [quality=0.95]
 * @returns {Promise<Blob>}
 */
export function canvasToBlob(canvas, type = 'image/png', quality = 0.95) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      },
      type,
      quality
    );
  });
}

/**
 * Create an offscreen canvas with optional initial content.
 * @param {number} width
 * @param {number} height
 * @returns {{ canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D }}
 */
export function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  return { canvas, ctx };
}

/**
 * Resize an image to fit within maxWidth/maxHeight while maintaining aspect ratio.
 * @param {HTMLImageElement|HTMLCanvasElement} source
 * @param {number} maxWidth
 * @param {number} maxHeight
 * @returns {HTMLCanvasElement}
 */
export function resizeImage(source, maxWidth, maxHeight) {
  const srcW = source.width || source.naturalWidth;
  const srcH = source.height || source.naturalHeight;
  const scale = Math.min(maxWidth / srcW, maxHeight / srcH, 1);
  const w = Math.round(srcW * scale);
  const h = Math.round(srcH * scale);
  const { canvas, ctx } = createCanvas(w, h);
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

/**
 * Crop an image to a region (normalised 0-1 coordinates).
 * @param {HTMLImageElement|HTMLCanvasElement} source
 * @param {{ x: number, y: number, width: number, height: number }} region - Normalised coords (0-1)
 * @param {number} [outputWidth] - Output width (defaults to source crop size)
 * @param {number} [outputHeight] - Output height (defaults to source crop size)
 * @returns {HTMLCanvasElement}
 */
export function cropImage(source, region, outputWidth, outputHeight) {
  const srcW = source.width || source.naturalWidth;
  const srcH = source.height || source.naturalHeight;
  const sx = region.x * srcW;
  const sy = region.y * srcH;
  const sw = region.width * srcW;
  const sh = region.height * srcH;
  const ow = outputWidth || Math.round(sw);
  const oh = outputHeight || Math.round(sh);
  const { canvas, ctx } = createCanvas(ow, oh);
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, ow, oh);
  return canvas;
}

/**
 * Download a canvas as a PNG file.
 * @param {HTMLCanvasElement} canvas
 * @param {string} [filename='banner.png']
 */
export async function downloadCanvas(canvas, filename = 'banner.png') {
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
